/**
 * `errors[]` on the M365 board — the channel that stops a partial read from
 * looking like a complete one.
 *
 * The backend fills `errors[]` when Graph answered but part of the answer could
 * not be read: a Message Center post whose `body` came back in an unexpected
 * shape, an entry it could not parse. `status_reason` stays `"ok"` in that case,
 * because the call itself worked — so without this alert the board would print
 * a smaller, quieter tenant than the real one and give no hint that it was
 * doing so. That is the flattering direction the design rules exist to prevent
 * (§5 "truncated queries surface in `errors[]` → an `Alert` on the board", §8
 * "a count is a lower bound until proven otherwise").
 *
 * ## One alert per payload, not one per board
 *
 * `M365ServiceHealth`, `M365Incidents` and `M365Messages` each carry their own
 * `errors[]`, and each is a separate Graph read that fails separately. Folding
 * them into a single board-level alert would file a dropped Message Center post
 * under the service tally, and leave the reader unable to tell which figures
 * are a floor — the criticism already made of the combined alert on the
 * Dynamics board. So this component renders once per payload, immediately above
 * the figures that payload feeds, and it names those figures.
 *
 * This board reads one of the three: `useM365ServiceHealth`. That payload feeds
 * every panel here — the summary strip, the service tiles, the open-issue table
 * and the resolved timeline — so exactly one instance renders, and its `affects`
 * list is the whole board. `/m365/incidents` and `/m365/messages` have no
 * consumer in this feature (see the note in `index.tsx`); whichever component
 * eventually renders them renders its own instance with its own `affects`.
 *
 * ## Follows the Dynamics pattern
 *
 * Same visual language as `FoErrorsAlert` in
 * `features/dynamics/access-panel.tsx`: a warning `Alert`, the backend's
 * strings verbatim, always open — never behind a disclosure. One deliberate
 * improvement: this alert names *which* figures it invalidates, using the exact
 * on-screen labels from `SUMMARY_LABELS`, which the Dynamics alert does not.
 */

import { Alert } from "@norican/react";

import type { M365ServiceHealth } from "@/lib/api";

import { SUMMARY_LABELS } from "./status";

interface M365ErrorsAlertProps {
  /** The payload's `errors[]` — problems that made this snapshot partial. */
  errors: readonly string[];
  /**
   * On-screen labels of the figures this payload feeds, so the alert can say
   * what it invalidates rather than leaving the reader to guess.
   *
   * Leave it empty when no figures are rendered at all (the not-configured /
   * permission-denied / upstream-error states): the copy then drops every
   * "lower bound" claim, because there is no number on screen to qualify.
   */
  affects?: readonly string[];
}

/**
 * The board's partial-read notice. Renders nothing for an empty `errors[]` —
 * no alert, no heading, no reserved space — because a complete read is the
 * normal case and should not cost a millimetre of the board.
 */
export function M365ErrorsAlert({ errors, affects = [] }: M365ErrorsAlertProps) {
  if (errors.length === 0) {
    return null;
  }

  const namesFigures = affects.length > 0;

  return (
    <Alert
      tone="warning"
      title={
        namesFigures
          ? "Partial read — the figures below are a floor, not a count"
          : "Part of the same Graph read also failed"
      }
    >
      <div className="flex flex-col gap-2 text-sm">
        <p className="text-fg-muted">
          {namesFigures ? (
            <>
              Microsoft Graph answered, but NOVA could not read all of it, so{" "}
              <span className="text-fg font-medium">{joinLabels(affects)}</span> cover only the
              entries that could be read. Each is a lower bound rather than a measurement: the true
              value is that or higher, never lower.
            </>
          ) : (
            <>
              These came back from the same snapshot as the state above. Nothing is measured in this
              state and no figures are shown, so this is extra evidence for whoever picks up the fix
              — not a qualified number.
            </>
          )}
        </p>
        {/* The backend already names the affected read in each line, so they go
            out verbatim — paraphrasing would lose the identifiers an operator
            needs to find the post or the service. */}
        <ul className="text-fg-muted flex list-disc flex-col gap-1 pl-5">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>
    </Alert>
  );
}

/**
 * Which figures a `/m365/service-health` payload puts on screen, by the label
 * the reader actually sees.
 *
 * Every panel on the live board is projected from this one payload, so a
 * dropped entry shortens all of them at once — there is no subset that stays
 * trustworthy while another is partial.
 */
export function affectedFigures(health: M365ServiceHealth): string[] {
  const labels = [
    SUMMARY_LABELS.servicesOperational,
    SUMMARY_LABELS.activeIncidents,
    SUMMARY_LABELS.activeAdvisories,
    SUMMARY_LABELS.noStatus,
    SUMMARY_LABELS.resolved(health.resolved_window_days),
    "the service tiles and issue lists beneath them",
    // A dropped issue entry shortens the MTTR sample too, so the medians are
    // drawn from fewer incidents than the tenant actually had.
    "the time-to-restore medians",
  ];

  // Only worth naming when the message-centre read was actually attempted:
  // when it was refused outright, the existing missing-scopes alert owns that
  // story and saying "partial" here would contradict it.
  if (health.messages_available) {
    labels.push("Microsoft's Message Center feed");
  }

  return labels;
}

/** "a, b and c" — an Oxford-comma-free list for prose, not a bullet list. */
function joinLabels(labels: readonly string[]): string {
  if (labels.length < 2) {
    return labels.join("");
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1) ?? ""}`;
}
